import { cx } from '@linaria/core';
import { memo, useRef, forwardRef, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2ConfigStore } from '@pm2/store';
import { useNavigationManager } from '@provider/navigation';
import { resets } from '@ui/css';
import { useChangeEffect, SizeTransition, IsomorphicPortal, CSSRefTransition, useStateRef } from '@utils/react';
import { GoToSearchItem } from './atom.go-to-search-item.js';
import { ResultItem } from './atom.result-item.js';
import { TRANSITION_DURATION_MS, wrapperCls, listCls, backdropCls, noResultsWrapperCls, headingCls, noResultsHeadingCls, noResultsListCls, noResultsItemCls } from './molecule.result-list.style.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

const MAX_RESULTS_IN_LIST = 5;
function ResultListUnmemoized({
  active,
  enteredQuery,
  currentQuery,
  results,
  onClose,
  onSelectionChange
}) {
  const rootRef = useRef(null);
  const selection = useResultSelection(rootRef, {
    enteredQuery,
    currentQuery,
    results,
    onClose
  });
  const filteredResults = getFilteredResults(results);
  const resultsKey = filteredResults.map(it => it.id).join(":");
  useChangeEffect(() => {
    if (selection?.id) {
      onSelectionChange(makeOptionId(selection.id));
    } else {
      onSelectionChange(makeOptionId("go-to-search"));
    }
  }, [selection]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(TransitionGroup, {
      component: null,
      children: active && /*#__PURE__*/jsx(SizeTransition, {
        axis: "vertical",
        timeout: TRANSITION_DURATION_MS,
        mountOnEnter: true,
        unmountOnExit: true,
        nodeRef: rootRef,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: wrapperCls,
          children: /*#__PURE__*/jsx(TransitionGroup, {
            component: null,
            children: /*#__PURE__*/jsx(SizeTransition, {
              axis: "vertical",
              swappable: true,
              classNames: listCls,
              timeout: TRANSITION_DURATION_MS,
              children: ref => filteredResults.length === 0 ? /*#__PURE__*/jsx(NoResultsMessage, {
                ref: ref,
                query: currentQuery
              }) : /*#__PURE__*/jsxs("ul", {
                ref: ref,
                className: listCls,
                id: "quicksearch-listbox",
                role: "listbox",
                "aria-label": "S\xF8geresultater",
                children: [filteredResults.map(result => /*#__PURE__*/jsx(ResultItem, {
                  id: makeOptionId(result.id),
                  result: result,
                  closeSearch: onClose,
                  selected: result.id === selection?.id
                }, result.id)), /*#__PURE__*/jsx(GoToSearchItem, {
                  id: makeOptionId("go-to-search"),
                  query: currentQuery,
                  count: results.length,
                  closeSearch: onClose,
                  selected: selection?.goToSearch === true
                })]
              })
            }, resultsKey)
          })
        })
      })
    }), /*#__PURE__*/jsx(IsomorphicPortal, {
      children: /*#__PURE__*/jsx(CSSRefTransition, {
        in: active,
        classNames: backdropCls,
        timeout: TRANSITION_DURATION_MS,
        mountOnEnter: true,
        unmountOnExit: true,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: cx(backdropCls, resets.rootCls),
          onClick: onClose,
          role: "none"
        })
      })
    })]
  });
}
const ResultList = /*#__PURE__*/memo(ResultListUnmemoized);
const NoResultsMessage = /*#__PURE__*/forwardRef(function NoResultsMessage({
  query
}, ref) {
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    className: cx(listCls, noResultsWrapperCls),
    children: [/*#__PURE__*/jsxs("h4", {
      className: headingCls,
      children: ["Din s\xF8gning for ", /*#__PURE__*/jsxs("b", {
        children: ["\"", query, "\""]
      }), " gav ingen resultater."]
    }), /*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx("h5", {
        className: noResultsHeadingCls,
        children: "Gode r\xE5d"
      }), /*#__PURE__*/jsxs("ul", {
        className: noResultsListCls,
        children: [/*#__PURE__*/jsx("li", {
          className: noResultsItemCls,
          children: "Tjek at du har stavet korrekt i din s\xF8gning"
        }), /*#__PURE__*/jsx("li", {
          className: noResultsItemCls,
          children: "Brug f\xE6rre s\xF8geord for at f\xE5 flere resultater i din s\xF8gning"
        }), /*#__PURE__*/jsx("li", {
          className: noResultsItemCls,
          children: "Fjern alle specialtegn i din s\xF8gning"
        }), /*#__PURE__*/jsx("li", {
          className: noResultsItemCls,
          children: "Pr\xF8v med mere generelle s\xF8geord i din s\xF8gning"
        })]
      })]
    })]
  });
});
function useResultSelection(rootRef, {
  enteredQuery,
  currentQuery,
  results,
  onClose
}) {
  const searchPath = pm2ConfigStore.useSelector(state => state.config.searchPath);
  const [selection, setSelection] = useStateRef(undefined);
  const navigationManager = useNavigationManager();
  useEffect(() => {
    // auto-clear selection whenever list of results changes
    setSelection(undefined);

    // extract list of visible results
    const filteredResults = getFilteredResults(results);

    // allow selection of results using arrow up/down
    const handleKeyDown = evt => {
      const selectedIndex = selection.current?.goToSearch ? filteredResults.length : filteredResults.findIndex(it => it.id === selection.current?.id);
      switch (evt.key) {
        case "ArrowUp":
          {
            evt.preventDefault();
            const nextIndex = selectedIndex > 0 ? selectedIndex - 1 : filteredResults.length;
            const nextResult = filteredResults[Number(nextIndex)];
            setSelection(nextResult ? {
              id: nextResult.id
            } : {
              goToSearch: true
            });
            break;
          }
        case "ArrowDown":
          {
            evt.preventDefault();
            const nextIndex = selectedIndex < filteredResults.length ? selectedIndex + 1 : 0;
            const nextResult = filteredResults[Number(nextIndex)];
            setSelection(nextResult ? {
              id: nextResult.id
            } : {
              goToSearch: true
            });
            break;
          }
        case "Enter":
          {
            evt.preventDefault();
            const selectedLink = rootRef.current?.querySelector(`[role="option"][aria-selected="true"]`)?.getAttribute("data-path") ?? `${searchPath}?q=${encodeURIComponent(enteredQuery)}`;
            navigationManager.push(selectedLink);
            onClose();
            break;
          }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [rootRef, selection, setSelection, navigationManager, results, currentQuery, enteredQuery, onClose, searchPath]);
  return selection.current;
}
function getFilteredResults(results) {
  return results.filter(it => it.type === "page").slice(0, MAX_RESULTS_IN_LIST);
}
function makeOptionId(id) {
  return `quicksearch-result-${id}`;
}

export { ResultList };
//# sourceMappingURL=molecule.result-list.js.map
